package dev.lingvo.site.components.editor

import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import dev.lingvo.shared.DecideProposalsRequest
import dev.lingvo.shared.GlossaryTermDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.MtProvider
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.PresenceDto
import dev.lingvo.shared.PresenceHeartbeatRequest
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProposalDto
import dev.lingvo.shared.ProviderReadiness
import dev.lingvo.shared.ProviderSettingsDto
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.ReviewStatus
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.TranslationOrigin
import dev.lingvo.shared.TranslationState
import dev.lingvo.shared.UpsertTranslationRequest
import dev.lingvo.shared.SourceTranslationRequest
import dev.lingvo.shared.isCanonicalSourceReviewId
import dev.lingvo.shared.nextMissingPlaceholder
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.site.components.source.validCurrentSource
import dev.lingvo.site.ds.LProgressCounts
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.ToastState
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.state.ProjectAuthority
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.supervisorScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import dev.lingvo.site.i18n.t

internal fun activeTargetValue(
    row: KeyRow,
    draft: EditorDraft,
    category: PluralCategory,
): String = if (row.key.isPlural) draft.pluralForms[category].orEmpty() else draft.value

internal fun activeSourceValue(row: KeyRow, category: PluralCategory): String =
    if (row.key.isPlural) row.sourceFor(category) else row.sourceText()

internal data class ConflictResolution(
    val row: KeyRow,
    val draft: EditorDraft,
)

internal fun resolveConflict(
    row: KeyRow,
    conflict: EditorConflict,
    latestDraft: EditorDraft?,
): ConflictResolution = ConflictResolution(
    row = row.copy(target = conflict.theirs),
    draft = (latestDraft ?: conflict.mine).copy(dirty = true, saving = false,
        targetRevision = conflict.theirs.revision, targetPresent = true, targetUpdatedAt = conflict.theirs.updatedAt, baseTarget = conflict.theirs),
)

internal fun sourceCopyValue(row: KeyRow, category: PluralCategory): String =
    if (row.key.isPlural) row.sourceFor(category) else row.sourceText()

internal fun isCurrentEditorPagingRequest(
    requestQuery: EditorQuery,
    requestGeneration: Long,
    currentQuery: EditorQuery,
    currentGeneration: Long,
): Boolean = requestQuery == currentQuery && requestGeneration == currentGeneration

internal fun isCurrentEditorQueryRequest(
    requestQuery: EditorQuery,
    requestRevision: Long,
    requestGeneration: Long,
    currentQuery: EditorQuery,
    currentRevision: Long,
    currentGeneration: Long,
): Boolean = requestQuery == currentQuery &&
    requestRevision == currentRevision &&
    requestGeneration == currentGeneration

@Stable
internal class TranslationEditorState(
    val projectSlug: String,
    val locale: String,
    initialKeyId: String?,
    initialSearch: String,
    /**
     * Filters carried in on the link, with no control in the editor to set or unset them.
     *
     * They are not defaults — they are the promise a work-queue row made about what this screen
     * would show. [clearFilters] is the only way back to everything, which is why the arriving
     * filter has to be visible: a filtered list with no matching control is its own trap.
     */
    private val initialReview: ReviewStatus? = null,
    private val initialStale: Boolean = false,
    private val scope: CoroutineScope,
    val toasts: ToastState,
    private val onConflictDetected: () -> Unit = {},
    private val onCompletion: (String) -> Unit = {},
    /**
     * How this editor leaves the page, for the one automation that has somewhere to send you.
     *
     * A hook rather than a router handle: the state is mounted directly by a dozen tests that
     * never route, and a `rememberPageContext()` read from in here would make every one of them
     * need a router to construct a domain object.
     */
    private val onNavigate: (String) -> Unit = {},
    private val draftStore: EditorDraftStore = BrowserEditorDraftStore,
    initialBootstrap: EditorBootstrap? = null,
) {
    var bootstrap by mutableStateOf(initialBootstrap?.takeIf { it.project.slug == projectSlug })
        private set
    private var presentationProject by mutableStateOf<ProjectDetailDto?>(null)
    private var foundationGeneration = 0L
    private var foundationProviderGeneration: Long? = null
    val project: ProjectDetailDto?
        get() = presentationProject
    val capability: ResolvedEditorCapability
        get() = bootstrap?.capability ?: ResolvedEditorCapability.ReadOnly
    val canWriteTranslations: Boolean
        get() = capability.canWriteTranslations
    val canApproveTranslations: Boolean
        get() = capability.canApproveTranslations
    val canEditStructure: Boolean
        get() = bootstrap?.canEditStructure == true
    var foundationLoading by mutableStateOf(true)
        private set
    var foundationError by mutableStateOf<String?>(null)
        private set
    var progress by mutableStateOf(LProgressCounts(0, 0, 0))
        private set

    var search by mutableStateOf(initialSearch)
    var debouncedSearch by mutableStateOf(initialSearch.trim())
    var stateFilter by mutableStateOf(EditorStateFilter.ALL)
    var fileId by mutableStateOf("")
    var tag by mutableStateOf("")
    var review by mutableStateOf(initialReview)
    var stale by mutableStateOf(initialStale)

    val rows = mutableStateListOf<KeyRow>()
    var total by mutableStateOf(0)
        private set
    var keysLoading by mutableStateOf(true)
        private set
    var loadingMore by mutableStateOf(false)
        private set
    var keysError by mutableStateOf<String?>(null)

    /**
     * The status behind [keysError], so a refusal can be told from a fault.
     *
     * `AsyncFailureState` derives `isRefusal` and `isMissing` from it; without one, a role revoked
     * mid-session and a deleted project both render as a retryable red alert. The editor is the
     * surface held open longest, so both are the likely cases rather than the exotic ones.
     */
    var keysErrorStatus by mutableStateOf<Int?>(null)
        private set
    var keysPagingError by mutableStateOf<String?>(null)
        private set
    var rowsAppliedQueryRevision by mutableStateOf<Long?>(null)
        private set

    var selectedKeyId by mutableStateOf<String?>(null)
        private set

    /**
     * Bumped every time an advancing save moves the queue on, so the view can put the caret in the
     * new target field.
     *
     * **Advancing used to lose focus entirely.** `advanceFrom` calls `selectKey`, which changes the
     * key and resets the plural category — and when the next key crosses the singular/plural
     * boundary the target field's element id changes with it, so the old `<textarea>` unmounts and
     * the browser drops focus on `<body>`. Mid-flow, in the loop PRODUCT.md promises can be
     * completed without a pointer, the ring vanished from the screen and the next Tab restarted at
     * the top of the document.
     *
     * A counter rather than a flag, and read from the view rather than pushed from here: this is
     * domain state and has no business knowing what a focus ring is, but every caller of an
     * advancing save — the two shortcuts and the action bar — needs the same thing to happen.
     */
    var advanceFocusRequest by mutableStateOf(0L)
        private set
    var pluralCategory by mutableStateOf(PluralCategory.OTHER)

    val drafts = mutableStateMapOf<String, EditorDraft>()
    val recoveredDrafts = mutableStateMapOf<String, Boolean>()
    val canonicalAcknowledgements = mutableStateMapOf<String, Pair<EditorDraft, TranslationDto>>()
    val sourceReview = EditorSourceReviewStore(projectSlug, locale, scope,
        targetWasPresent = { keyId -> drafts[keyId]?.targetPresent == true },
        rowSource = { keyId -> rowFor(keyId)?.validSourceSnapshot() },
        onRead = { keyId, change ->
            canonicalAcknowledgements.remove(keyId)?.let { (sent, saved) -> acceptCanonicalAcknowledgement(keyId, sent, saved, change) }
        },
        onReadFailure = { keyId -> retainCanonicalAcknowledgement(keyId) },
    )
    var sourceAwareQueueCaptured by mutableStateOf(Session.serverFeatures.sourceAwareReview)
        private set
    val qaIssues = mutableStateListOf<QaIssueDto>()
    var providers by mutableStateOf<List<ProviderSettingsDto>>(emptyList())
        private set
    var providersLoading by mutableStateOf(false)
        private set
    var providersUnavailable by mutableStateOf(false)
        private set
    var glossary by mutableStateOf<List<GlossaryTermDto>>(emptyList())
        private set

    /**
     * Proposals waiting on strings in this locale, by key id.
     *
     * Loaded with the foundation rather than when a string is selected: an agent's suggestion has to
     * be visible **in the editor**, or a translator retypes by hand what a machine already proposed
     * and the whole propose-then-review loop is decorative.
     */
    var proposalsByKey by mutableStateOf<Map<String, ProposalDto>>(emptyMap())
        private set

    private var assistTerminalFeedbackOwner: EditorAssistStatusSession? = null
    val assistStatuses = EditorAssistStatusStore()
    val panels = EditorPanelStore(
        projectSlug = projectSlug,
        locale = locale,
        scope = scope,
        toasts = toasts,
        canWriteTranslations = { canWriteTranslations },
        currentKeyId = { selectedKeyId },
        shouldToastAssistOutcomes = { assistTerminalFeedbackOwner == null },
        assistStatuses = assistStatuses,
    )
    val proposalActions = EditorProposalActionStore(
        canWriteTranslations = { canWriteTranslations },
        assistStatuses = assistStatuses,
        transport = { claim ->
            ApiClient.decideProposals(
                claim.projectSlug,
                DecideProposalsRequest(ids = listOf(claim.proposalId), accept = claim.accept),
            )
        },
    )

    /**
     * The queue header's two bulk automations, and the run either can have in flight.
     *
     * Owned by the domain object rather than the composable so a run survives the queue being
     * unmounted — which the compact layout does every time somebody opens a string. A poller
     * cancelled by a layout change would leave a run going on the server with no progress readout
     * and no receipt anywhere.
     */
    val queueAutomation: EditorQueueAutomationStore = EditorQueueAutomationStore(
        projectSlug = projectSlug,
        locale = locale,
        scope = scope,
        toasts = toasts,
        onReload = {
            // Retained: `loadKeys` restores this query from its cache and replaces in place, so the
            // queue never blanks. `loadFoundation` is what moves the header's counts, which a run
            // has just changed.
            scope.launch {
                loadKeys()
                loadFoundation()
            }
        },
        onOpenReceipt = { jobId -> onNavigate("/app/projects/$projectSlug/data/jobs/$jobId") },
        onShowKeys = { keyIds -> keyIds.firstOrNull()?.let(::selectKey) },
    )

    /**
     * Leaves the editor for a route the overflow menu names.
     *
     * The hook is private because nothing outside this object should be able to *change* where the
     * editor goes; this is the read side of it, for the three overflow rows (review queue, QA page,
     * pre-translate) that are doors rather than actions. In a fixture with no router it is a no-op,
     * which is the right answer for a test that mounts the domain without a page around it.
     */
    internal fun navigateTo(path: String) = onNavigate(path)

    internal val requestedKey = EditorRequestedKeyStore(this, scope, initialKeyId)
    private val rowCache = mutableMapOf<String, KeyRow>()
    private val queryCache = mutableMapOf<EditorQuery, CachedRows>()
    private val autosaveJobs = mutableMapOf<String, Job>()
    private val saveMutexes = mutableMapOf<String, Mutex>()

    internal fun rowFor(keyId: String): KeyRow? = rowCache[keyId]
    internal fun pauseSourceAutosave(keyId: String) { autosaveJobs.remove(keyId)?.cancel() }
    internal fun focusSourceProblem(keyId: String) { if (keyId == selectedKeyId) onConflictDetected() }
    internal fun sourceCompletion(message: String) { onCompletion(message) }
    internal fun resumeSourceAutosave(keyId: String) {
        if (drafts[keyId]?.dirty == true && drafts[keyId]?.saving != true) scheduleAutosave(keyId)
    }
    internal fun markTargetAbsent(keyId: String) {
        val row = rowCache[keyId] ?: return
        row.target?.let { progress = adjustProgress(progress, it.state, TranslationState.EMPTY) }
        replaceRow(row.copy(target = null))
    }

    val currentQuery: EditorQuery
        get() = EditorQuery(debouncedSearch, stateFilter, fileId, tag, review, stale)

    val selectedRow: KeyRow?
        get() = selectedKeyId?.let(rowCache::get)

    val selectedDraft: EditorDraft?
        get() = selectedKeyId?.let(drafts::get)

    val hasUnsavedDrafts: Boolean
        get() = canWriteTranslations && drafts.values.any { it.dirty || it.saving }

    val enabledProviders: List<MtProvider>
        get() = providers
            .filter { settings -> settings.readiness == ProviderReadiness.READY }
            .map(ProviderSettingsDto::provider)

    val availableTags: List<String>
        get() = rows.flatMap { it.key.tags }.distinct().sorted()

    val currentAssistAttentionCount: Int
        get() {
            val row = selectedRow ?: return 0
            val comments = (panels.comments[row.key.id] as? Async.Success)?.value
            return assistAttentionCount(
                hasProposal = proposalsByKey.containsKey(row.key.id),
                comments = comments,
            )
        }

    internal fun clearFilter(filter: EditorFilterId) {
        when (filter) {
            EditorFilterId.STATE -> stateFilter = EditorStateFilter.ALL
            EditorFilterId.FILE -> fileId = ""
            EditorFilterId.TAG -> tag = ""
            EditorFilterId.REVIEW -> review = null
            EditorFilterId.STALE -> stale = false
        }
    }

    /**
     * Back to every string in this locale.
     *
     * One method rather than a lambda per pane, because there were two and they already disagreed —
     * one cleared `debouncedSearch` and the other did not, so clearing from the key list left a
     * search still applied for one debounce cycle. It also has to clear the link-borne filters:
     * those have no control anywhere, so a "clear filters" that left them on would be a button that
     * visibly does nothing.
     */
    fun clearFilters() {
        search = ""
        debouncedSearch = ""
        stateFilter = EditorStateFilter.ALL
        fileId = ""
        tag = ""
        review = null
        stale = false
    }

    suspend fun loadFoundation(verifiedProject: ProjectDetailDto? = null) {
        val generation = ++foundationGeneration
        val metadata = EditorMetadataRead(projectSlug, bootstrap?.project) { generation == foundationGeneration }
        if (!metadata.isCurrent()) return
        foundationLoading = project == null
        foundationError = null
        try {
            // The route bootstrap resolves authority. Counts and files need a current detail;
            // a legitimate pre-read caller can supply that detail without a duplicate request.
            val loadedProject = metadata.validate(verifiedProject ?: metadata.request { ApiClient.getProject(projectSlug) })
            val verifiedRoute = metadata.verifiedRoute
            if (bootstrap == null) {
                val exactRole = verifiedRoute
                    ?.takeIf { route ->
                        route.project.id == loadedProject.id &&
                            route.project.orgId == loadedProject.orgId &&
                            route.project.slug == loadedProject.slug
                    }
                    ?.workspace
                    ?.role
                publishBootstrap(
                    EditorBootstrap(
                        project = loadedProject,
                        capability = resolveEditorCapability(loadedProject.editorCapabilities, exactRole),
                        canEditStructure = ProjectAuthority(exactRole).canEditStructure,
                    ),
                )
            }
            presentationProject = loadedProject
            progress = progressCounts(loadedProject.progress.firstOrNull { it.locale == locale })
            supervisorScope {
                val qaRequest = async { metadata.request { ApiClient.qa(projectSlug, locale) } }
                foundationProviderGeneration = generation
                providersLoading = true
                val providerRequest = async { metadata.request { ApiClient.listProviders(projectSlug) } }
                val glossaryRequest = async { metadata.request { ApiClient.listGlossaryTerms(projectSlug) } }
                val proposalRequest = async { metadata.request { ApiClient.proposals(projectSlug, locale) } }

                val issues = runSecondaryList("QA", qaRequest, metadata)
                metadata.requireCurrent()
                qaIssues.replaceWith(issues)
                providers = try {
                    providerRequest.await().also {
                        metadata.requireCurrent()
                        providersUnavailable = false
                    }
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    metadata.requireCurrent()
                    providersUnavailable = true
                    toasts.show(
                        tr("editor.translation_editor_state.couldnt_load_translation_providers_the_editor", "Couldn't load translation providers. The editor is still available."),
                        LToastVariant.WARNING,
                    )
                    emptyList()
                } finally {
                    if (metadata.isCurrent() && foundationProviderGeneration == generation) {
                        foundationProviderGeneration = null
                        providersLoading = false
                    }
                }
                val terms = runSecondaryList("glossary", glossaryRequest, metadata)
                metadata.requireCurrent()
                glossary = terms
                // Newest wins when an agent has proposed the same string twice: the later one is
                // the one it would stand behind.
                val proposals = try {
                    proposalRequest.await().proposals
                } catch (cancelled: CancellationException) {
                    throw cancelled
                } catch (_: Throwable) {
                    metadata.requireCurrent()
                    emptyList()
                }
                metadata.requireCurrent()
                proposalsByKey = proposals.associateBy { it.keyId }
            }
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (!metadata.isCurrent()) return
            presentationProject = null
            foundationError = failure.message?.takeIf(String::isNotBlank)
                ?: "Couldn't load this translation project. Retry?"
        } finally {
            if (metadata.isCurrent()) {
                foundationLoading = false
                // A failed project read may supersede a foundation provider phase before it
                // starts its own. Independent retryProviders work has no foundation owner.
                if (foundationProviderGeneration != null) {
                    foundationProviderGeneration = null
                    providersLoading = false
                }
            }
        }
    }

    /**
     * Accepts or rejects the proposal on one string, without leaving the editor.
     *
     * Accepting writes through the same save path as typing it, so the approval gate, the QA checks
     * and the history entry are identical to a human having written the words — because as far as
     * everything downstream is concerned, a human just did.
     */
    fun decideProposal(keyId: String, accept: Boolean) {
        val claim = currentProposalActionClaim(keyId, accept) ?: return
        val request = proposalActions.begin(claim) { currentProposalActionClaim(keyId, accept) }
            ?: return
        scope.launch {
            proposalActions.execute(
                request = request,
                currentClaim = { currentProposalActionClaim(keyId, accept) },
                onSuccess = { settled ->
                    proposalsByKey = proposalsByKey - settled.keyId
                    if (settled.accept) {
                        if (assistTerminalFeedbackOwner == null) {
                            toasts.show(tr("editor.translation_editor_state.accepted", "Accepted"), LToastVariant.SUCCESS)
                        }
                        // The proposal publication above is one non-suspending commit. Reconcile
                        // server-owned translation/progress data separately so a later key switch
                        // cannot delay or duplicate that action's map/toast publication.
                        scope.launch { loadKeys(reset = true) }
                    } else {
                        if (assistTerminalFeedbackOwner == null) {
                            toasts.show(tr("editor.translation_editor_state.rejected", "Rejected"), LToastVariant.NEUTRAL)
                        }
                    }
                },
                onFailure = { failed, failure ->
                    if (assistTerminalFeedbackOwner == null) {
                        toasts.show(
                            proposalFailureMessage(failed, failure),
                            LToastVariant.DANGER,
                        )
                    }
                },
            )
        }
    }

    private fun currentProposalActionClaim(
        keyId: String,
        accept: Boolean,
    ): EditorProposalActionClaim? {
        if (!canWriteTranslations || selectedKeyId != keyId) return null
        val proposal = proposalsByKey[keyId]
            ?.takeIf { it.keyId == keyId && it.locale == locale }
            ?: return null
        return EditorProposalActionClaim(
            projectSlug = projectSlug,
            locale = locale,
            keyId = keyId,
            proposalId = proposal.id,
            accept = accept,
        )
    }

    suspend fun loadKeys(
        reset: Boolean = true,
        queryRevision: Long = activeQueueQueryRevision,
    ) {
        if (Session.serverFeatures.sourceAwareReview) sourceAwareQueueCaptured = true
        val query = currentQuery
        if (reset && queryRevision < activeQueueQueryRevision) return
        if (reset) activeQueueQueryRevision = queryRevision
        val requestGeneration = if (reset) {
            pagingGeneration += 1
            pagingGeneration
        } else {
            pagingGeneration
        }

        fun requestIsCurrent(): Boolean = isCurrentEditorQueryRequest(
            requestQuery = query,
            requestRevision = queryRevision,
            requestGeneration = requestGeneration,
            currentQuery = currentQuery,
            currentRevision = activeQueueQueryRevision,
            currentGeneration = pagingGeneration,
        )

        if (reset) {
            // Cleared the moment the query changes. A cached restore renders instantly and the
            // refetch replaces it a moment later — and in that window the old query's cursor would
            // still be live, so a scroll could page one filter using another's position.
            loadingMore = false
            nextCursor = null
            val cached = queryCache[query]
            if (cached == null) {
                keysLoading = true
                rows.clear()
                total = 0
                rowsAppliedQueryRevision = null
            } else {
                applyRows(cached.items, cached.total, replace = true)
                keysLoading = false
                rowsAppliedQueryRevision = queryRevision
            }
            keysError = null
            keysErrorStatus = null
            keysPagingError = null
        }
        try {
            val response = ApiClient.listKeys(
                projectSlug = projectSlug,
                locale = locale,
                fileId = query.fileId.ifBlank { null },
                search = query.search.ifBlank { null },
                state = query.state.apiState,
                tag = query.tag.ifBlank { null },
                review = query.review,
                stale = query.stale,
                limit = EDITOR_PAGE_SIZE,
            )
            if (!requestIsCurrent()) return
            // The cursor for the next page, kept per query so switching filters cannot page one
            // filter with another's position.
            nextCursor = response.nextCursor
            queryCache[query] = CachedRows(response.items, response.total)
            applyRows(response.items, response.total, replace = true)
            rowsAppliedQueryRevision = queryRevision
            requestedKey.resolve(::requestIsCurrent)
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (failure: Throwable) {
            if (requestIsCurrent() && rows.isEmpty()) {
                keysErrorStatus = (failure as? ApiException)?.status
                keysError = failure.message?.takeIf(String::isNotBlank)
                    ?: "Couldn't load translation keys. Retry?"
            }
        } finally {
            if (requestIsCurrent()) keysLoading = false
        }
    }

    fun loadMore(afterLoad: (() -> Unit)? = null) {
        // `nextCursor == null` is the authority on whether more exists, not `rows.size >= total`:
        // under a filter that is shrinking as someone works, the total moves and the comparison
        // stops early while rows remain.
        if (loadingMore || keysLoading || nextCursor == null) {
            afterLoad?.invoke()
            return
        }
        val query = currentQuery
        val cursor = nextCursor
        val requestGeneration = pagingGeneration
        val requestRevision = activeQueueQueryRevision
        loadingMore = true
        scope.launch {
            try {
                if (!isCurrentEditorQueryRequest(
                        requestQuery = query,
                        requestRevision = requestRevision,
                        requestGeneration = requestGeneration,
                        currentQuery = currentQuery,
                        currentRevision = activeQueueQueryRevision,
                        currentGeneration = pagingGeneration,
                    )
                ) {
                    return@launch
                }
                keysPagingError = null
                val response = ApiClient.listKeys(
                    projectSlug = projectSlug,
                    locale = locale,
                    fileId = query.fileId.ifBlank { null },
                    search = query.search.ifBlank { null },
                    state = query.state.apiState,
                    tag = query.tag.ifBlank { null },
                    review = query.review,
                    stale = query.stale,
                    limit = EDITOR_PAGE_SIZE,
                    // A cursor rather than an offset. Approving ten strings under an "untranslated"
                    // filter used to shift every later offset by ten, so ten rows were skipped — and
                    // because `applyRows` dedupes by id they were never rendered at all. Somebody
                    // could work a filter to zero and leave ten strings untranslated.
                    after = cursor,
                )
                if (!isCurrentEditorQueryRequest(
                        requestQuery = query,
                        requestRevision = requestRevision,
                        requestGeneration = requestGeneration,
                        currentQuery = currentQuery,
                        currentRevision = activeQueueQueryRevision,
                        currentGeneration = pagingGeneration,
                    )
                ) {
                    return@launch
                }
                nextCursor = response.nextCursor
                applyRows(response.items, response.total, replace = false)
                queryCache[query] = CachedRows(rows.toList(), response.total)
                if (isCurrentEditorQueryRequest(
                        requestQuery = query,
                        requestRevision = requestRevision,
                        requestGeneration = requestGeneration,
                        currentQuery = currentQuery,
                        currentRevision = activeQueueQueryRevision,
                        currentGeneration = pagingGeneration,
                    )
                ) {
                    afterLoad?.invoke()
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: Throwable) {
                if (isCurrentEditorQueryRequest(
                        requestQuery = query,
                        requestRevision = requestRevision,
                        requestGeneration = requestGeneration,
                        currentQuery = currentQuery,
                        currentRevision = activeQueueQueryRevision,
                        currentGeneration = pagingGeneration,
                    )
                ) {
                    keysPagingError = failure.message?.takeIf(String::isNotBlank)
                        ?: "Couldn't load more keys. Retry?"
                    toasts.show(keysPagingError.orEmpty(), LToastVariant.DANGER)
                }
            } finally {
                if (isCurrentEditorQueryRequest(
                        requestQuery = query,
                        requestRevision = requestRevision,
                        requestGeneration = requestGeneration,
                        currentQuery = currentQuery,
                        currentRevision = activeQueueQueryRevision,
                        currentGeneration = pagingGeneration,
                    )
                ) {
                    loadingMore = false
                }
            }
        }
    }

    fun selectKey(keyId: String) {
        val row = rowCache[keyId] ?: return
        requestedKey.selected(keyId)
        // **The recovery notice dies when you leave the key, and nowhere else.**
        //
        // It sits above the source card and the target field, so its removal moves both. Every
        // other moment in the cycle has the caret in that field — typing, and the autosave 800ms
        // after typing stops. Leaving the key is the one point at which the whole pane is being
        // replaced anyway, so a change in height there is invisible.
        //
        // It also keeps the notice honest for as long as it is on screen: the text you are looking
        // at *was* restored from this browser, whatever has been saved since.
        selectedKeyId?.takeIf { it != keyId }?.let(recoveredDrafts::remove)
        selectedKeyId = keyId
        drafts.getOrPut(keyId) { initialDraft(row, locale) }
        pluralCategory = pluralCategoriesFor(locale).firstOrNull() ?: PluralCategory.OTHER
        panels.ensureAssistLoaded(row)
        sourceReview.ensureLoaded(row)
    }

    internal fun acquireAssistTerminalFeedback(session: EditorAssistStatusSession) {
        val previous = assistTerminalFeedbackOwner
        if (previous == session) return
        if (previous != null) releaseAssistTerminalFeedback(previous)
        assistTerminalFeedbackOwner = session
    }

    internal fun releaseAssistTerminalFeedback(session: EditorAssistStatusSession) {
        if (assistTerminalFeedbackOwner == session) {
            assistTerminalFeedbackOwner = null
        }
        assistStatuses.drainUncommitted(session)
            .mapNotNull(EditorAssistStatusPublication::toastFeedback)
            .forEach { feedback ->
                toasts.show(feedback.message, feedback.variant)
            }
        assistStatuses.retireSession(session)
    }

    fun retryProviders() {
        if (providersLoading) return
        providersLoading = true
        scope.launch {
            try {
                providers = ApiClient.listProviders(projectSlug)
                providersUnavailable = false
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
                providersUnavailable = true
                toasts.show(
                    tr("editor.translation_editor_state.couldnt_load_translation_providers_retry", "Couldn't load translation providers. Retry?"),
                    LToastVariant.WARNING,
                )
            } finally {
                providersLoading = false
            }
        }
    }

    fun moveSelection(delta: Int) {
        if (rows.isEmpty()) return
        val current = rows.indexOfFirst { it.key.id == selectedKeyId }.coerceAtLeast(0)
        val target = current + delta
        when {
            target in rows.indices -> selectKey(rows[target].key.id)
            target >= rows.size && rows.size < total -> loadMore {
                rows.getOrNull(target)?.let { selectKey(it.key.id) }
            }
        }
    }

    fun updateValue(value: String) {
        if (!canWriteTranslations) return
        val keyId = selectedKeyId ?: return
        val draft = drafts[keyId] ?: return
        updateDraft(keyId, draft.copy(
            value = value,
            dirty = true,
            provider = null,
            unsafeSuggestion = false,
        ))
        scheduleAutosave(keyId)
    }

    fun updatePlural(category: PluralCategory, value: String) {
        if (!canWriteTranslations) return
        val keyId = selectedKeyId ?: return
        val draft = drafts[keyId] ?: return
        updateDraft(keyId, draft.copy(
            pluralForms = draft.pluralForms + (category to value),
            dirty = true,
            provider = null,
            unsafeSuggestion = false,
        ))
        scheduleAutosave(keyId)
    }

    fun clearCurrent() {
        if (!canWriteTranslations) return
        val row = selectedRow ?: return
        val draft = selectedDraft ?: return
        updateDraft(row.key.id, if (row.key.isPlural) {
            draft.copy(
                pluralForms = pluralCategoriesFor(locale).associateWith { "" },
                dirty = true,
                provider = null,
                unsafeSuggestion = false,
            )
        } else {
            draft.copy(value = "", dirty = true, provider = null, unsafeSuggestion = false)
        })
        scheduleAutosave(row.key.id)
    }

    fun sourceCopyText(): String? = selectedRow
        ?.let { sourceCopyValue(it, pluralCategory) }
        ?.takeIf(String::isNotEmpty)

    fun insertAtSelection(
        text: String,
        selection: EditorTargetSelection?,
    ): EditorTargetSelectionRestore? {
        if (!canWriteTranslations) return null
        val row = selectedRow ?: return null
        val draft = selectedDraft ?: return null
        val target = EditorFocusRef.Target(
            keyId = row.key.id,
            pluralCategory = pluralCategory.takeIf { row.key.isPlural },
            locale = locale,
        )
        val current = activeTargetValue(row, draft, pluralCategory)
        val validSelection = selection?.takeIf { it.target == target }
        val sourceValue = validSelection?.value ?: current
        val insertion = insertText(
            value = sourceValue,
            selectionStart = validSelection?.selectionStart ?: sourceValue.length,
            selectionEnd = validSelection?.selectionEnd ?: sourceValue.length,
            insertion = text,
        )
        if (row.key.isPlural) {
            updatePlural(pluralCategory, insertion.value)
        } else {
            updateValue(insertion.value)
        }
        return EditorTargetSelectionRestore(target, insertion.caret)
    }

    fun insertSuggestion(suggestion: EditorSuggestion): EditorSuggestionInsertion? {
        if (!canWriteTranslations) return null
        val row = selectedRow ?: return null
        val draft = selectedDraft ?: return null
        val provider = (suggestion as? EditorSuggestion.Machine)?.provider
        val unsafe = suggestion.warning != null
        updateDraft(row.key.id, if (row.key.isPlural) {
            val categories = pluralCategoriesFor(locale)
            draft.copy(
                pluralForms = categories.associateWith { category ->
                    suggestion.pluralForms?.get(category).orEmpty()
                },
                dirty = true,
                provider = provider,
                unsafeSuggestion = unsafe,
            )
        } else {
            draft.copy(
                value = suggestion.value.orEmpty(),
                dirty = true,
                provider = provider,
                unsafeSuggestion = unsafe,
            )
        })
        // Cancel first, unconditionally. Typing schedules an autosave 800ms out; accepting a
        // suggestion the QA checks flagged within that window left the earlier job in flight, and it
        // then saved the very text this branch refuses to autosave. Skipping the schedule is not
        // enough — the pending one has to die.
        autosaveJobs.remove(row.key.id)?.cancel()
        if (!unsafe) scheduleAutosave(row.key.id)
        return EditorSuggestionInsertion(
            keyId = row.key.id,
            pluralCategory = pluralCategory.takeIf { row.key.isPlural },
            unsafe = unsafe,
        )
    }

    fun saveCurrent(approve: Boolean, advance: Boolean, announce: Boolean = true) {
        if (!canWriteTranslations || (approve && !canApproveTranslations)) return
        val keyId = selectedKeyId ?: return
        autosaveJobs.remove(keyId)?.cancel()
        scope.launch { saveKey(keyId, approve, advance, announce) }
    }

    /**
     * The next placeholder from the source that the target has not used, in source order.
     *
     * Backs `Alt+P` and the button beside the punctuation strip. Null when the translation already
     * carries them all, so the control disappears rather than offering a duplicate.
     */
    fun nextPlaceholder(): String? {
        val row = selectedRow ?: return null
        val draft = selectedDraft ?: return null
        return nextMissingPlaceholder(
            activeSourceValue(row, pluralCategory),
            activeTargetValue(row, draft, pluralCategory),
        )
    }

    /**
     * Unresolved collisions, one slot per key rather than one for the whole editor.
     *
     * This used to be a single `EditorConflict?`. That was safe only as long as a conflict could
     * only ever land on the key you were still looking at — true before the optimistic advance
     * (`24ab714`), which moves you to the next key before the save's response comes back. After
     * that fix, a 409 for the key you just left overwrote a still-unresolved conflict on the key
     * before it, silently dropping the earlier one. Held in state rather than shown as a toast: a
     * toast is dismissed by looking away, and the choice between two versions of somebody's work
     * is not something to lose by accident.
     */
    val conflicts = mutableStateMapOf<String, EditorConflict>()

    /** Keep what I typed, over their version. Re-saves against their version as the base. */
    fun resolveConflictKeepMine(keyId: String) {
        if (!canWriteTranslations) return
        val pending = conflicts[keyId] ?: return
        rowCache[keyId]?.let { row ->
            val resolution = resolveConflict(
                row = row,
                conflict = pending,
                latestDraft = drafts[keyId],
            )
            replaceRow(resolution.row)
            drafts[keyId] = resolution.draft
            persistDraft(keyId, resolution.draft, resolution.row.target?.updatedAt)
        }
        conflicts.remove(keyId)
        sourceReview.targetAbsent.remove(keyId)
        if (sourceReview.pauseCodes[keyId] in listOf("translation_conflict", "stale_translation")) {
            sourceReview.pauses.remove(keyId)
            sourceReview.pauseCodes.remove(keyId)
        }
        // Re-save the key being resolved, not `saveCurrent()` — that saves whatever is
        // *currently selected*, and by the time someone clicks "Keep mine" on a card the caret has
        // very often already advanced to a different key. Saving the selection here would resave
        // the wrong translation, or silently do nothing if the click race outpaced selection.
        autosaveJobs.remove(keyId)?.cancel()
        scope.launch { saveKey(keyId, approve = false, advance = false, announce = true) }
    }

    /** Take theirs and drop what I typed. Only ever from an explicit click. */
    fun resolveConflictTakeTheirs(keyId: String) {
        if (!canWriteTranslations) return
        val pending = conflicts.remove(keyId) ?: return
        val row = rowCache[keyId] ?: return
        replaceRow(row.copy(target = pending.theirs))
        sourceReview.targetAbsent.remove(keyId)
        drafts[keyId] = initialDraft(row.copy(target = pending.theirs), locale).let { clean ->
            if (sourceBoundMode(pending.mine)) clean.copy(sourceSnapshot = pending.mine.sourceSnapshot, sourceClaimCaptured = true) else clean
        }
        if (sourceReview.pauseCodes[keyId] in listOf("stale_translation", "translation_conflict")) {
            sourceReview.pauses.remove(keyId); sourceReview.pauseCodes.remove(keyId)
        }
        recoveredDrafts.remove(keyId)
        draftStore.remove(projectSlug, locale, keyId)
    }

    /** Rebase the next manual save on theirs while leaving the latest local draft unsaved. */
    fun dismissConflict(keyId: String) {
        if (!canWriteTranslations) return
        val pending = conflicts[keyId] ?: return
        rowCache[keyId]?.let { row ->
            val resolution = resolveConflict(
                row = row,
                conflict = pending,
                latestDraft = drafts[keyId],
            )
            replaceRow(resolution.row)
            drafts[keyId] = resolution.draft
            persistDraft(keyId, resolution.draft, resolution.row.target?.updatedAt)
        }
        conflicts.remove(keyId)
        if (sourceReview.pauseCodes[keyId] in listOf("stale_translation", "translation_conflict")) {
            sourceReview.pauses.remove(keyId); sourceReview.pauseCodes.remove(keyId)
        }
    }

    /**
     * Who else has the visible strings open.
     *
     * Empty until a heartbeat answers, and empty again the moment one fails. Presence must never be
     * the reason an editor looks broken.
     */
    var presence by mutableStateOf<Map<String, List<PresenceDto>>>(emptyMap())
        private set

    /**
     * Reports this person's focus and picks up everyone else's.
     *
     * Called on focus, on blur, and on a timer while a field is focused. Silent on failure — the
     * whole feature degrades to the behaviour that existed before it, which is the only acceptable
     * failure mode for a line of advisory text.
     */
    fun heartbeatPresence(keyId: String, released: Boolean = false) {
        scope.launch {
            val response = try {
                ApiClient.presence(
                    projectSlug = projectSlug,
                    request = PresenceHeartbeatRequest(
                        keyId = keyId.orEmpty(),
                        locale = locale,
                        released = released,
                        // The keys on screen, so one request answers both questions.
                        visibleKeyIds = rows.map { it.key.id },
                    ),
                )
            } catch (cancelled: CancellationException) {
                // `runCatching` used to swallow this. Presence is the most-cancelled call on the
                // screen — every twenty seconds, on every key switch, and on every dispose — and a
                // caught `CancellationException` breaks structured cancellation inside
                // `scope.launch`: the coroutine carries on past a scope that is already dead.
                throw cancelled
            } catch (_: Throwable) {
                // Silent, as before. The whole feature degrades to the behaviour that existed before
                // it, which is the only acceptable failure mode for a line of advisory text.
                return@launch
            }
            presence = response.editing
        }
    }

    /** Where the next page starts, or null when the list is exhausted. */
    private var pagingGeneration = 0L
    private var activeQueueQueryRevision = 0L
    private var nextCursor: String? = null

    private fun scheduleAutosave(keyId: String) {
        if (!canWriteTranslations) return
        autosaveJobs.remove(keyId)?.cancel()
        if (sourceReview.reaffirming[keyId] == true) return
        if (drafts[keyId]?.unsafeSuggestion == true) return
        if (sourceSaveProblem(keyId) != null) return
        autosaveJobs[keyId] = scope.launch {
            delay(800)
            saveKey(keyId, approve = false, advance = false, announce = false)
        }
    }

    private suspend fun saveKey(keyId: String, approve: Boolean, advance: Boolean, announce: Boolean) {
        if (!canWriteTranslations || (approve && !canApproveTranslations)) return
        val mutex = saveMutexes.getOrPut(keyId) { Mutex() }
        mutex.withLock {
            if (!canWriteTranslations || (approve && !canApproveTranslations)) return
            // An acknowledged canonical save still needs its complete confirming read. Waiting
            // does not create a refusal that could outlive that read and block the next edit.
            if (canonicalAcknowledgements.containsKey(keyId)) {
                pauseSourceAutosave(keyId)
                return
            }
            val oldRow = rowCache[keyId] ?: return
            val snapshot = drafts[keyId] ?: initialDraft(oldRow, locale)
            sourceSaveProblem(keyId)?.let { problem ->
                sourceReview.pauses[keyId] = problem
                pauseSourceAutosave(keyId)
                return
            }
            if (approve && inlineQa(oldRow, snapshot, locale).any { it.severity == QaSeverity.ERROR }) {
                toasts.show(tr("editor.translation_editor_state.resolve_the_qa_errors_before_approving", "Resolve the QA errors before approving this translation."), LToastVariant.WARNING)
                return
            }
            if (!snapshot.dirty && (!approve || oldRow.target?.state == TranslationState.APPROVED)) {
                // Nothing was written, so only the advance is worth reporting. ⌘S is a reflex —
                // people press it after every sentence, and on a draft that is already saved it
                // reached here and raised a 4.3-second animated toast saying "Saved" about a
                // request that never left the browser. The action bar two centimetres away already
                // reads "Saved" the whole time (`editorSaveStatus`), and it did not change, so the
                // panel was movement in service of repeating something still on screen. ⌘Enter
                // keeps its announcement because the selection genuinely moved and a screen reader
                // has no other way to learn that.
                announceSaveCompletion(oldRow, approve, advance, announce && advance)
                if (advance) advanceFrom(keyId)
                return
            }

            val oldState = oldRow.target?.state ?: TranslationState.EMPTY
            val newState = snapshot.requestState(oldRow.key.isPlural, approve)
            val optimistic = optimisticTranslation(oldRow, snapshot, newState)
            replaceRow(oldRow.copy(target = optimistic))
            progress = adjustProgress(progress, oldState, newState)
            drafts[keyId] = snapshot.copy(saving = true)
            // Advance the instant the optimistic row lands, not after the round trip. The
            // failure paths below roll back per keyId, not per current selection, so a save
            // that fails after the user has already moved on still restores the right row.
            if (advance) advanceFrom(keyId)

            try {
                val saved = if (sourceBoundMode(snapshot)) ApiClient.updateTranslationForSource(
                    projectSlug, keyId, locale,
                    SourceTranslationRequest(
                        sourceView = checkNotNull(snapshot.sourceSnapshot).view,
                        expectedTargetRevision = snapshot.targetRevision,
                        value = snapshot.value.takeUnless { oldRow.key.isPlural },
                        pluralForms = snapshot.pluralForms.takeIf { oldRow.key.isPlural },
                        state = newState,
                    ),
                ) else ApiClient.updateTranslation(
                    projectSlug = projectSlug,
                    keyId = keyId,
                    locale = locale,
                    request = UpsertTranslationRequest(
                        value = snapshot.value.takeUnless { oldRow.key.isPlural },
                        pluralForms = snapshot.pluralForms.takeIf { oldRow.key.isPlural },
                        state = newState,
                    ),
                    // The version this edit started from. Sent on every save, so a collision is
                    // refused rather than silently winning.
                    ifMatch = snapshot.targetUpdatedAt ?: oldRow.target?.updatedAt,
                )
                if (sourceBoundMode(snapshot) && !guardedSaveReceiptMatches(keyId, locale, snapshot, newState, saved)) throw unavailableSourceReview()
                val cached = rowCache[keyId] ?: oldRow
                if (cached.target?.revision == snapshot.targetRevision || cached.target?.revision == saved.revision) {
                    replaceRow(cached.copy(target = saved))
                }
                // **Not cleared here, and the previous comment on this line was wrong.**
                //
                // It claimed a save is "the one moment in the cycle when the reader is not
                // part-way through a word". The common save is the autosave — `scheduleAutosave`
                // fires 800ms after the last keystroke — so clearing here collapsed ~120px above
                // the caret while somebody paused to think. That is the same jump the first fix
                // was for, moved from the first keystroke to the first pause and made worse: at
                // least a keystroke is something the reader did.
                //
                // Cleared when the selection leaves the key instead; see `selectKey`.
                val latest = drafts[keyId] ?: snapshot
                val unchanged = latest.value == snapshot.value && latest.pluralForms == snapshot.pluralForms
                val stillOwnTarget = latest.targetRevision == snapshot.targetRevision
                drafts[keyId] = latest.copy(
                    dirty = if (unchanged) false else latest.dirty,
                    saving = false,
                    provider = if (unchanged) null else latest.provider,
                    unsafeSuggestion = if (unchanged) false else latest.unsafeSuggestion,
                    targetRevision = if (stillOwnTarget) saved.revision else latest.targetRevision,
                    targetPresent = if (stillOwnTarget) true else latest.targetPresent,
                    targetUpdatedAt = if (stillOwnTarget) saved.updatedAt else latest.targetUpdatedAt,
                    baseTarget = if (stillOwnTarget) saved else latest.baseTarget,
                )
                val canonicalConfirmation = sourceBoundMode(snapshot) && locale == snapshot.sourceSnapshot?.view?.sourceLocale &&
                    !unchangedTargetReceipt(snapshot, saved)
                if (canonicalConfirmation) {
                    canonicalAcknowledgements[keyId] = snapshot to saved
                    persistDraft(keyId, drafts.getValue(keyId), saved.updatedAt, forceRecovery = true)
                }
                if (sourceBoundMode(snapshot)) {
                    sourceReview.loaded(keyId)?.let { evidence ->
                        val basis = snapshot.sourceSnapshot?.takeIf { it.view.sourceRevisionId == saved.sourceRevisionId }
                            ?: evidence.earlierSource?.takeIf { it.view.sourceRevisionId == saved.sourceRevisionId }
                        if (evidence.target?.revision == snapshot.targetRevision || evidence.target?.revision == saved.revision) {
                            sourceReview.changes[keyId] = Async.Success(evidence.copy(target = saved, earlierSource = basis))
                        }
                    }
                    if (canonicalConfirmation) sourceReview.ensureLoaded(rowFor(keyId) ?: oldRow, retry = true)
                }
                if (unchanged) {
                    recoveredDrafts.remove(keyId)
                    if (!canonicalConfirmation) draftStore.remove(projectSlug, locale, keyId)
                } else {
                    persistDraft(keyId, drafts.getValue(keyId), saved.updatedAt)
                }
                qaIssues.removeAll { it.keyId == keyId }
                panels.refreshHistory(keyId)
                refreshQa(rows.firstOrNull { it.key.id == keyId }?.key?.name)
                announceSaveCompletion(oldRow, approve, advance, announce)
            } catch (cancelled: CancellationException) {
                rollbackOwnOptimistic(keyId, oldRow, optimistic)
                progress = adjustProgress(progress, newState, oldState)
                drafts[keyId] = (drafts[keyId] ?: snapshot).copy(saving = false, dirty = true)
                persistDraft(keyId, drafts.getValue(keyId), oldRow.target?.updatedAt)
                throw cancelled
            } catch (failure: ApiException) {
                rollbackOwnOptimistic(keyId, oldRow, optimistic)
                progress = adjustProgress(progress, newState, oldState)
                // The draft stays dirty and stays in `drafts`, so a refresh does not lose what was
                // typed. Nothing is discarded without a click — that is the whole rule here.
                drafts[keyId] = (drafts[keyId] ?: snapshot).copy(saving = false, dirty = true)
                persistDraft(keyId, drafts.getValue(keyId), oldRow.target?.updatedAt)
                if (sourceBoundMode(snapshot)) {
                    sourceReview.refused(keyId, failure, snapshot.targetPresent)
                    pauseSourceAutosave(keyId)
                    if (keyId == selectedKeyId && failure.code in listOf("source_changed", "source_view_unavailable")) onConflictDetected()
                }
                when {
                    failure.status == 409 && failure.code in listOf("translation_conflict", "stale_translation") -> {
                        // Somebody else saved while this was being typed. Both versions are kept
                        // and a person chooses; the alternative is the loser finding out in the
                        // History tab, if ever.
                        val theirs = if (sourceBoundMode(snapshot)) {
                            sourceReview.loaded(keyId)?.takeIf { it.validCurrentSource()?.view == currentSourceFor(rowCache[keyId] ?: oldRow)?.view }?.target
                        } else failure.currentTranslation()
                        // Naming the key rather than saying "this translation": the optimistic
                        // advance above means the reader has usually already moved on to the next
                        // key by the time this 409 lands, so "this" no longer points at anything on
                        // screen.
                        val keyName = rows.firstOrNull { it.key.id == keyId }?.key?.name ?: keyId
                        if (theirs != null) {
                            conflicts[keyId] = EditorConflict(
                                keyId = keyId,
                                mine = snapshot,
                                theirs = theirs,
                            )
                            // Only steal focus into the card when its key is still the one on
                            // screen. The optimistic advance above means the reader has usually
                            // already moved on to the next key by the time this 409 lands;
                            // `enterConflict` closes whatever utility panel is open and moves
                            // focus, which would yank the reader out of the key they are now
                            // typing into for a card that is not even mounted there.
                            if (keyId == selectedKeyId) onConflictDetected()
                            // Persistent (durationMillis = 0): the same optimistic advance means
                            // this toast, not the card, is very often the only notice of a
                            // divergent save, for a key that may no longer be on screen. A toast
                            // that erases itself after four seconds would be the only record of a
                            // save that silently failed.
                            toasts.show(
                                trf(
                                    "editor.translation_editor_state.someone_else_changed_key_open_to_choose",
                                    "Someone else changed {key}. Your text is safe — open that key " +
                                        "to choose.",
                                    "key" to keyName,
                                ),
                                LToastVariant.WARNING,
                                durationMillis = 0,
                            )
                        } else {
                            // No card can be built without the server's version, but the save still
                            // silently diverged — the notice needs to survive just as long.
                            toasts.show(
                                trf(
                                    "editor.translation_editor_state.someone_else_changed_key_reload",
                                    "Someone else changed {key}. Reload to see their version.",
                                    "key" to keyName,
                                ),
                                LToastVariant.WARNING,
                                durationMillis = 0,
                            )
                        }
                    }
                    // The approval gate. The server lists which checks failed, and repeating its
                    // message beats inventing a vaguer one.
                    failure.status == 422 -> toasts.show(failure.message, LToastVariant.WARNING)
                    failure.status == 403 && failure.code == "approval_not_permitted" ->
                        toasts.show(failure.message, LToastVariant.WARNING)
                    else -> toasts.show(tr("editor.translation_editor_state.couldnt_save_this_translation_retry", "Couldn't save this translation. Retry?"), LToastVariant.DANGER)
                }
            } catch (_: Throwable) {
                rollbackOwnOptimistic(keyId, oldRow, optimistic)
                progress = adjustProgress(progress, newState, oldState)
                drafts[keyId] = (drafts[keyId] ?: snapshot).copy(saving = false, dirty = true)
                persistDraft(keyId, drafts.getValue(keyId), oldRow.target?.updatedAt)
                if (sourceBoundMode(snapshot)) {
                    sourceReview.pauses[keyId] = unavailableSourceReviewMessage()
                    pauseSourceAutosave(keyId)
                }
                toasts.show(
                    tr("editor.translation_editor_state.couldnt_save_this_translation_retry", "Couldn't save this translation. Retry?"),
                    LToastVariant.DANGER,
                )
            }
        }
    }

    private fun announceSaveCompletion(
        row: KeyRow,
        approve: Boolean,
        advance: Boolean,
        announce: Boolean,
    ) {
        if (!announce) return
        // **Both branches through the keyed inline channel, and no toast on either.**
        //
        // A non-advancing save — ⌘S, the single most repeated keystroke in this product — used to
        // raise an animated global success toast. Three things were wrong with that at once. The
        // motion contract says routine editor completion is instant and keyed to the initiating
        // key, and lists optimistic save advancement under what never animates. The first stated
        // rule of the system is that nothing competing with typing animates, and a translator
        // presses this mid-sentence. And the toast was saying what the action bar already says
        // inline and without motion: the field's own status reads "Saved" the moment the draft
        // settles.
        //
        // Removing it outright would have been worse than leaving it, because that inline status is
        // a plain `Span` with no live region — the toast was carrying the only announcement a
        // screen reader got. `onCompletion` is the channel the advancing save already uses: a
        // visually-hidden `role="status"`, keyed by announcement id so saving the same key twice
        // announces twice. Routing both branches through it keeps the announcement, drops the
        // motion, and leaves one mechanism instead of two.
        onCompletion(
            if (advance) editorCompletionMessage(row.key.name, approve) else editorSaveConfirmation(approve),
        )
    }

    private fun rollbackOwnOptimistic(keyId: String, before: KeyRow, optimistic: TranslationDto) {
        val cached = rowCache[keyId] ?: return
        if (cached.target?.revision == optimistic.revision && cached.target?.updatedAt == optimistic.updatedAt) {
            replaceRow(cached.copy(target = before.target))
        }
    }

    private fun optimisticTranslation(
        row: KeyRow,
        draft: EditorDraft,
        state: TranslationState,
    ): TranslationDto = row.target?.copy(
        value = draft.value.takeUnless { row.key.isPlural },
        pluralForms = draft.pluralForms.takeIf { row.key.isPlural },
        origin = if (draft.provider == null) TranslationOrigin.HUMAN else TranslationOrigin.MACHINE,
        mtProvider = draft.provider,
        state = state,
    ) ?: TranslationDto(
        id = "optimistic-${row.key.id}",
        keyId = row.key.id,
        locale = locale,
        value = draft.value.takeUnless { row.key.isPlural },
        pluralForms = draft.pluralForms.takeIf { row.key.isPlural },
        origin = if (draft.provider == null) TranslationOrigin.HUMAN else TranslationOrigin.MACHINE,
        mtProvider = draft.provider,
        state = state,
        updatedAt = "",
    )

    internal fun applyRows(items: List<KeyRow>, newTotal: Int, replace: Boolean) {
        if (Session.serverFeatures.sourceAwareReview || items.any { it.sourceSnapshot != null }) sourceAwareQueueCaptured = true
        if (replace) rows.clear()
        val knownIds = rows.mapTo(mutableSetOf()) { it.key.id }
        items.filterNot { it.key.id in knownIds }.forEach { row ->
            rows += row
            rowCache[row.key.id] = row
            val sourceChange = sourceReview.loaded(row.key.id)
            if (sourceChange != null && sourceChange.currentSource?.view != row.sourceSnapshot?.view) {
                sourceReview.ensureLoaded(row, retry = true)
            }
            if (canWriteTranslations) {
                drafts.getOrPut(row.key.id) { recoverDraft(row) }
                if (sourceSaveProblem(row.key.id) != null) pauseSourceAutosave(row.key.id)
            } else {
                // Read-only state follows the latest server row and never adopts or deletes a
                // browser draft that belongs to a writer session.
                drafts[row.key.id] = initialDraft(row, locale)
                recoveredDrafts.remove(row.key.id)
                conflicts.remove(row.key.id)
            }
        }
        total = newTotal
        val requested = requestedKey.pendingId?.let { id -> rows.firstOrNull { it.key.id == id } }
        when {
            requested != null -> {
                selectKey(requested.key.id)
            }
            requestedKey.pendingId == null && !requestedKey.preservesSelection() &&
                (selectedKeyId == null || rows.none { it.key.id == selectedKeyId }) -> {
                rows.firstOrNull()?.let { selectKey(it.key.id) }
            }
        }
    }

    /** A requested UUID is cached without changing the filtered queue's coverage or cursor. */
    internal fun cacheExactRequestedRow(row: KeyRow) {
        rowCache[row.key.id] = row
        if (canWriteTranslations) {
            drafts.getOrPut(row.key.id) { recoverDraft(row) }
            if (sourceSaveProblem(row.key.id) != null) pauseSourceAutosave(row.key.id)
        } else {
            drafts[row.key.id] = initialDraft(row, locale)
            recoveredDrafts.remove(row.key.id)
            conflicts.remove(row.key.id)
        }
    }

    internal fun exactKeyRequestOwnership(): () -> Boolean {
        val query = currentQuery
        val revision = activeQueueQueryRevision
        val generation = pagingGeneration
        return {
            isCurrentEditorQueryRequest(query, revision, generation, currentQuery, activeQueueQueryRevision, pagingGeneration)
        }
    }

    internal fun replaceRow(row: KeyRow) {
        rowCache[row.key.id] = row
        val index = rows.indexOfFirst { it.key.id == row.key.id }
        if (index >= 0) rows[index] = row
    }

    private fun updateDraft(keyId: String, draft: EditorDraft) {
        if (!canWriteTranslations) return
        drafts[keyId] = draft
        // **The recovery notice does not die on a keystroke.**
        //
        // `recoveredDrafts.remove(keyId)` used to be here, so the first character typed unmounted
        // the "Draft recovered" alert above the fields — roughly 100px of card plus a 24px gap —
        // and the source card and the target textarea jumped up under the caret, mid-word. Every
        // plural form did it too, since `updatePlural` takes this same path.
        //
        // The flag means "this text was restored from your browser", and typing does not make that
        // untrue. It stops being true when the text is written, so it is cleared on a successful
        // save instead — a moment nobody is mid-word. Nothing animates either way: rule one is
        // that nothing competing with typing moves.
        persistDraft(keyId, draft, rowCache[keyId]?.target?.updatedAt)
    }

    internal fun persistDraft(keyId: String, draft: EditorDraft, baseRevision: String? = draft.targetUpdatedAt, forceRecovery: Boolean = false) {
        if (!canWriteTranslations) return
        if (!forceRecovery && !draft.dirty && !draft.saving) {
            draftStore.remove(projectSlug, locale, keyId)
            return
        }
        draftStore.save(
            projectSlug = projectSlug,
            locale = locale,
            keyId = keyId,
            draft = StoredEditorDraft(draft.targetUpdatedAt ?: baseRevision, draft.copy(saving = false, dirty = true)),
        )
    }

    private fun recoverDraft(row: KeyRow): EditorDraft {
        val current = initialDraft(row, locale).let { draft ->
            if (sourceAwareQueueCaptured) draft.copy(sourceClaimCaptured = true, sourceSnapshot = row.validSourceSnapshot()) else draft
        }
        if (!canWriteTranslations) return current
        val loaded = draftStore.load(projectSlug, locale, row.key.id) ?: return current
        val stored = loaded.copy(draft = loaded.draft.copy(sourceSnapshot = loaded.draft.sourceSnapshot?.takeIf { it.view.keyId == row.key.id },
            targetPresent = loaded.draft.targetPresent || loaded.baseRevision != null))
        return when (decideEditorDraftRecovery(stored, current, row.target?.updatedAt)) {
            EditorDraftRecoveryDecision.REMOVE -> {
                draftStore.remove(projectSlug, locale, row.key.id)
                current
            }
            EditorDraftRecoveryDecision.RESTORE -> {
                recoveredDrafts[row.key.id] = true
                stored.draft.copy(dirty = true, saving = false, targetUpdatedAt = stored.draft.targetUpdatedAt ?: stored.baseRevision,
                    sourceClaimCaptured = stored.draft.sourceClaimCaptured || sourceAwareQueueCaptured)
            }
            EditorDraftRecoveryDecision.CONFLICT -> {
                recoveredDrafts[row.key.id] = true
                row.target?.let { translation ->
                    conflicts[row.key.id] = EditorConflict(row.key.id, stored.draft, translation)
                }
                stored.draft.copy(dirty = true, saving = false, unsafeSuggestion = true, targetUpdatedAt = stored.draft.targetUpdatedAt ?: stored.baseRevision,
                    sourceClaimCaptured = stored.draft.sourceClaimCaptured || sourceAwareQueueCaptured)
            }
        }
    }

    private fun publishBootstrap(value: EditorBootstrap) {
        if (bootstrap != null) return
        bootstrap = value
        if (!value.capability.canWriteTranslations) return

        // The focused screen can load rows while its direct project-detail fallback is still in
        // flight. No edit is permitted during that fail-closed window, so these are still pristine
        // server drafts and can safely adopt any stored writer draft once authority is known.
        rows.forEach { row -> drafts[row.key.id] = recoverDraft(row) }
    }

    /**
     * Moves to the next loaded row after the current one that satisfies [predicate], if there is one.
     *
     * The sibling of [advanceFrom], which walks to the literal next row. This is what "Next
     * untranslated" and "Next QA issue" are: the same walk with a question asked at each step, and
     * a wrap to the top so the last match on the page is not a dead end. Returns false when nothing
     * on the loaded page matches, which is the caller's cue to say so rather than move the caret
     * silently.
     */
    internal fun advanceTo(predicate: (KeyRow) -> Boolean): Boolean {
        if (rows.isEmpty()) return false
        val start = rows.indexOfFirst { it.key.id == selectedKeyId }
        val order = (1..rows.size).map { offset -> rows[(start + offset).mod(rows.size)] }
        val match = order.firstOrNull(predicate) ?: return false
        if (match.key.id == selectedKeyId) return false
        selectKey(match.key.id)
        advanceFocusRequest += 1
        return true
    }

    private fun advanceFrom(keyId: String) {
        val index = rows.indexOfFirst { it.key.id == keyId }
        val nextIndex = index + 1
        rows.getOrNull(nextIndex)?.let {
            selectKey(it.key.id)
            advanceFocusRequest += 1
            return
        }
        if (rows.size < total) loadMore {
            rows.getOrNull(nextIndex)?.let {
                selectKey(it.key.id)
                advanceFocusRequest += 1
            }
        }
    }

    /**
     * Re-check one string, or the whole project when no string is named.
     *
     * Saving a translation used to re-run the QA engine over every key in the project to refresh
     * the panel for the one row that changed — on a 10,000-key project, ten thousand evaluations
     * per save. Only the saved string's issues can have changed, and the row above already dropped
     * its stale ones, so this asks about that string alone and merges the answer back.
     */
    private fun refreshQa(keyName: String? = null) {
        scope.launch {
            delay(120)
            try {
                val fresh = ApiClient.qa(projectSlug, locale, keyName)
                if (keyName == null) {
                    qaIssues.replaceWith(fresh)
                } else {
                    qaIssues.addAll(fresh)
                }
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (_: Throwable) {
            }
        }
    }

    private suspend fun <T> runSecondaryList(
        label: String,
        request: kotlinx.coroutines.Deferred<List<T>>,
        metadata: EditorMetadataRead,
    ): List<T> {
        return try {
            request.await()
        } catch (cancelled: CancellationException) {
            throw cancelled
        } catch (_: Throwable) {
            metadata.requireCurrent()
            toasts.show(
                trf(
                    "editor.translation_editor_state.couldnt_load_label_editor_still_available",
                    "Couldn't load {label}. The editor is still available.",
                    "label" to label,
                ),
                LToastVariant.WARNING,
            )
            emptyList()
        }
    }

    private fun <T> MutableList<T>.replaceWith(values: List<T>) {
        clear()
        addAll(values)
    }

    private data class CachedRows(
        val items: List<KeyRow>,
        val total: Int,
    )
}
